import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  RotateCw, 
  RotateCcw, 
  FlipHorizontal2, 
  FlipVertical2,
  Type,
  ArrowRight,
  Square,
  Circle,
  Pencil,
  Undo2,
  Redo2,
  Droplet,
  Download,
  Sparkles,
  ImageIcon
} from "lucide-react";

interface EditorToolbarProps {
  onRotateRight: () => void;
  onRotateLeft: () => void;
  onFlipHorizontal: () => void;
  onFlipVertical: () => void;
  onSelectTool: (tool: string) => void;
  activeTool: string;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onAutoEnhance: () => void;
  onCompareToggle: () => void;
  compareMode: boolean;
}

export const EditorToolbar = ({
  onRotateRight,
  onRotateLeft,
  onFlipHorizontal,
  onFlipVertical,
  onSelectTool,
  activeTool,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onAutoEnhance,
  onCompareToggle,
  compareMode,
}: EditorToolbarProps) => {
  return (
    <div className="flex items-center gap-2 p-2 bg-background border-b">
      {/* Transform tools */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRotateLeft}
          title="Rotate Left"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRotateRight}
          title="Rotate Right"
        >
          <RotateCw className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onFlipHorizontal}
          title="Flip Horizontal"
        >
          <FlipHorizontal2 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onFlipVertical}
          title="Flip Vertical"
        >
          <FlipVertical2 className="w-4 h-4" />
        </Button>
      </div>

      <Separator orientation="vertical" className="h-6" />

      {/* Annotation tools */}
      <div className="flex items-center gap-1">
        <Button
          variant={activeTool === 'text' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onSelectTool('text')}
          title="Add Text"
        >
          <Type className="w-4 h-4" />
        </Button>
        <Button
          variant={activeTool === 'arrow' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onSelectTool('arrow')}
          title="Draw Arrow"
        >
          <ArrowRight className="w-4 h-4" />
        </Button>
        <Button
          variant={activeTool === 'rectangle' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onSelectTool('rectangle')}
          title="Draw Rectangle"
        >
          <Square className="w-4 h-4" />
        </Button>
        <Button
          variant={activeTool === 'circle' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onSelectTool('circle')}
          title="Draw Circle"
        >
          <Circle className="w-4 h-4" />
        </Button>
        <Button
          variant={activeTool === 'freehand' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onSelectTool('freehand')}
          title="Freehand Draw"
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          variant={activeTool === 'pin' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onSelectTool('pin')}
          title="Place Pin"
        >
          📍
        </Button>
      </div>

      <Separator orientation="vertical" className="h-6" />

      {/* History */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
        >
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
        >
          <Redo2 className="w-4 h-4" />
        </Button>
      </div>

      <Separator orientation="vertical" className="h-6" />

      {/* Enhancement */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onAutoEnhance}
        title="Auto Enhance"
      >
        <Sparkles className="w-4 h-4" />
      </Button>

      <Separator orientation="vertical" className="h-6" />

      {/* Compare mode */}
      <Button
        variant={compareMode ? 'secondary' : 'ghost'}
        size="sm"
        onClick={onCompareToggle}
        title="Before/After Compare"
      >
        <ImageIcon className="w-4 h-4" />
      </Button>
    </div>
  );
};
